import { TutorBillingExportType } from '@brighterly/lib-core-types/tutor/enums'

import { useListsStore } from '@shared/model'

import { TutorBillingModal } from './enums'
import { useTutorBillingStore } from './store'
import { useTutorBillingExport } from '../api/export.hook'
import { TUTOR_BILLING_EXPORT_COLUMNS } from '../config/tutor-billing'
import { resolveTutorBillingExportTypeOptions } from '../lib/tutor-billing-export'

interface ModalTutorBillingExportReturn {
  isOpen: ComputedRef<boolean>
  isSaving: ComputedRef<boolean>
  title: ComputedRef<string>
  subtitle: ComputedRef<string>
  hasType: ComputedRef<boolean>
  columns: ComputedRef<string[]>
  setClose: () => void
  setSend: () => Promise<void>
}

export const useModalTutorBillingExport = (): ModalTutorBillingExportReturn => {
  const listsStore = useListsStore()
  const store = useTutorBillingStore()

  const isOpen = computed<boolean>(() => store.isModal(TutorBillingModal.Export))
  const isSaving = computed<boolean>(() => store.isSaving)

  const title = computed<string>(() => {
    const type = store.getExportType
    if (!type) return 'Export'

    const label = resolveTutorBillingExportTypeOptions(listsStore.getTutorBillingExportTypeOptions)
      .find(option => option.value === type)?.text ?? type

    return `Export ${label} — file preview`
  })

  const subtitle = computed<string>(() => {
    const base = 'Column structure of the .csv. Values are filled in at export time.'

    return store.getExportType === TutorBillingExportType.NewAccounts
      ? `${base} Only records without a Salesforce account are included.`
      : base
  })

  const hasType = computed<boolean>(() => !!store.getExportType)

  const columns = computed<string[]>(() => {
    const type = store.getExportType
    return type ? TUTOR_BILLING_EXPORT_COLUMNS[type] ?? [] : []
  })

  const setClose = (): void => {
    if (store.isSaving) return
    store.closeModal()
  }

  const setSend = async (): Promise<void> => {
    const type = store.getExportType
    if (!type || store.isSaving) return

    const sent = await useTutorBillingExport(type)

    if (sent && store.isModalFor(TutorBillingModal.Export, null)) store.closeModal()
  }

  return {
    isOpen,
    isSaving,
    title,
    subtitle,
    hasType,
    columns,
    setClose,
    setSend,
  }
}
